import { FC, /* useState */ } from 'react';
import { BvIcon } from 'bevi-icon';
import { INSS_MARGIN_PER_PAGE, /* InssMarginItem, */ InssMarginTab } from '../../../models/InssMargin/InssMarginModels';
import { useInssMargin } from '../../../providers/InssMargin/InssMarginProvider';
import InssMarginIndicators from './InssMarginIndicators';
import { InssMarginDiscardedRow, InssMarginOpportunityRow } from './InssMarginRow';
// TODO(AI-1338): editar dados do cliente desligado no lançamento — descomentar no futuro.
// import InssMarginRecordModal from './InssMarginRecordModal';
import './InssMargin.scss';

const TABS: { id: InssMarginTab; label: string }[] = [
    { id: 'oportunidades', label: 'Oportunidades' },
    // TODO(AI-1338): aba Arquivadas desligada no lançamento (sem arquivar ela perde o
    // sentido) — descomentar no futuro junto com o botão de arquivar (InssMarginRow).
    // { id: 'descartadas', label: 'Arquivadas' },
];

// Pré-digitação do Aumento de Margem INSS (E1): carteira do agente logado. Aberto pelo
// item do menu lateral (ver ASSISTANT_BEHAVIOR em Chats.tsx). Sem simulação/digitação
// nesta entrega.
const InssMargin: FC = () => {
    const {
        tab,
        page,
        lastPage,
        search,
        items,
        totals,
        total,
        emptyMessage,
        isInitialLoading,
        isListLoading,
        changeTab,
        goToPage,
        changeSearch,
        indicators,
    } = useInssMargin();
    // TODO(AI-1338): editar dados do cliente desligado no lançamento — descomentar no futuro.
    // const [recordTarget, setRecordTarget] = useState<{ item: InssMarginItem; opener: HTMLElement } | null>(null);

    if (isInitialLoading) {
        return (
            <div className="inss-margin inss-margin--loading" role="status" aria-live="polite">
                <span className="inss-margin__spin inss-margin__spin--lg" aria-hidden="true" />
                <p className="inss-margin__loading-title">Carregando suas oportunidades</p>
                <p className="inss-margin__loading-sub">Buscando os clientes da sua carteira</p>
            </div>
        );
    }

    const isOpportunities = tab === 'oportunidades';
    const firstShown = items.length ? (page - 1) * INSS_MARGIN_PER_PAGE + 1 : 0;
    const lastShown = items.length ? firstShown + items.length - 1 : 0;
    const hasPrevious = page > 1;
    const hasNext = page < lastPage;
    const tabCount = (id: InssMarginTab) => totals[id] ?? 0;

    return (
        <div className="inss-margin">
            {indicators && <InssMarginIndicators indicators={indicators} />}

            <div className="inss-margin__tabs" role="tablist" aria-label="Carteira de clientes">
                {TABS.map(({ id, label }) => {
                    const selected = tab === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            id={`inss-margin-tab-${id}`}
                            aria-selected={selected}
                            aria-controls="inss-margin-panel"
                            className={`inss-margin__tab${selected ? ' is-on' : ''}`}
                            onClick={() => changeTab(id)}
                        >
                            {label} <span className="inss-margin__tab-count">{tabCount(id)}</span>
                        </button>
                    );
                })}
            </div>

            <div
                id="inss-margin-panel"
                role="tabpanel"
                aria-labelledby={`inss-margin-tab-${tab}`}
                className="inss-margin__panel"
                aria-busy={isListLoading}
            >
                <div className="inss-margin__toolbar">
                    <div className="inss-margin__search">
                        <BvIcon name="search" variant="solid" size={0.9} />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => changeSearch(e.target.value)}
                            placeholder="Buscar por nome ou CPF"
                            aria-label="Buscar por nome ou CPF"
                        />
                    </div>
                </div>

                {!items.length && !isListLoading ? (
                    <div className="inss-margin__empty">
                        {search.trim() ? 'Nenhum cliente corresponde à busca.' : emptyMessage}
                    </div>
                ) : (
                    <div className={`inss-margin__table-box${isListLoading ? ' is-loading' : ''}`}>
                        {isListLoading && (
                            <div className="inss-margin__table-loading">
                                <span className="inss-margin__spin" aria-hidden="true" />
                            </div>
                        )}
                        {/* Rola só a tabela (cabeçalho fixo); o overlay de carregamento fica fora dela. */}
                        <div className="inss-margin__table-scroll">
                            <table className="inss-margin__table">
                                <thead>
                                    {isOpportunities ? (
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Celular</th>
                                            <th>Financeiras</th>
                                            {/* TODO(AI-1338): coluna de ações (editar/arquivar) desligada no lançamento — descomentar no futuro. */}
                                            {/* <th><span className="visually-hidden">Ações</span></th> */}
                                        </tr>
                                    ) : (
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Celular</th>
                                            <th>Motivo</th>
                                            <th>Arquivada em</th>
                                            <th><span className="visually-hidden">Ações</span></th>
                                        </tr>
                                    )}
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        isOpportunities ? (
                                            <InssMarginOpportunityRow
                                                key={item.id}
                                                item={item}
                                                // TODO(AI-1338): editar desligado no lançamento — descomentar no futuro.
                                                // onOpenRecord={(target, opener) => setRecordTarget({ item: target, opener })}
                                            />
                                        ) : (
                                            <InssMarginDiscardedRow key={item.id} item={item} />
                                        )
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {total > 0 && (
                    <div className="inss-margin__pager">
                        <span>Mostrando {firstShown}–{lastShown} de {total}</span>
                        <div className="inss-margin__pager-buttons">
                            <button
                                type="button"
                                className="inss-margin__btn inss-margin__btn--secondary"
                                onClick={() => goToPage(page - 1)}
                                disabled={!hasPrevious || isListLoading}
                            >
                                Anterior
                            </button>
                            <button
                                type="button"
                                className="inss-margin__btn inss-margin__btn--secondary"
                                onClick={() => goToPage(page + 1)}
                                disabled={!hasNext || isListLoading}
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* TODO(AI-1338): editar dados do cliente desligado no lançamento — descomentar no futuro. */}
            {/* {recordTarget && (
                <InssMarginRecordModal
                    item={recordTarget.item}
                    opener={recordTarget.opener}
                    onClose={() => setRecordTarget(null)}
                />
            )} */}
        </div>
    );
};

export default InssMargin;
